import React, { useEffect, useRef, useState } from "react";
import {
    Button,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    MessageBar,
    MessageBarBody,
    OverlayDrawer,
    Spinner,
    Text,
    tokens,
} from "@fluentui/react-components";
import { Dismiss24Regular } from "@fluentui/react-icons";
import { Separator } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import { DebtPaymentImputation } from "../../../../models/Entities/CashFlow/Payment/DebtPaymentImputation";

// styles
import './PaymentImputation.Form.Styles.css';

// component functions
import * as ComponentFunctions from './PaymentImputation.Form.Function';

// components
import DebtSelectorComponent from "../../../../commons/component/DebtSelector/DebtSelector.Component";

const PaymentImputationFormComponent = (props: ComponentFunctions.Props) => {
    const [t] = useTranslation();
    const [pendingImputations, setPendingImputations] = useState<DebtPaymentImputation[]>([]);
    const justSubmittedRef = useRef(false);

    const payer = props.payment ? props.GetPersonByIdentifier(props.payment.payer) : undefined;
    const receiver = props.payment ? props.GetPersonByIdentifier(props.payment.receiver) : undefined;
    const pendingAmount = props.payment?.pendingIndictmentAmount ?? 0;
    const hasPendingAmount = pendingAmount > 0 && !!payer;

    useEffect(() => {
        if (props.successCreatingImputation && justSubmittedRef.current) {
            justSubmittedRef.current = false;
            setPendingImputations([]);
            if (props.payment) {
                props.GetPaymentImputations(props.payment.id);
            }
        }
    }, [props.successCreatingImputation]);

    const handleConfirm = () => {
        if (!props.payment || pendingImputations.length === 0) return;
        justSubmittedRef.current = true;
        pendingImputations.forEach(imp => {
            props.CreatePaymentImputation({
                paymentId: props.payment!.id,
                id: 0,
                amount: imp.amount,
                currencyCode: null,
                debtsPaymentImputation: [{ debtId: imp.debtId, amount: imp.amount, exchangeRate: 1 }],
            });
        });
    };

    const handleClose = () => {
        setPendingImputations([]);
        props.onClose();
    };

    if (!props.payment) return null;

    return (
        <OverlayDrawer
            open={props.isOpen}
            onOpenChange={(_, data) => { if (!data.open) handleClose(); }}
            position="end"
            size="large"
        >
            <DrawerHeader>
                <DrawerHeaderTitle
                    action={
                        <Button
                            appearance="subtle"
                            aria-label={t('common.close', 'Cerrar')}
                            icon={<Dismiss24Regular />}
                            onClick={handleClose}
                        />
                    }
                >
                    {props.title ?? t('cashflow.imputations.formTitle', 'Imputar saldo pendiente')}
                </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody>
                <div className="paymentImputationFormDrawerBody">

                    {hasPendingAmount ? (
                        <>
                            <div className="paymentImputationFormSummary">
                                <Text size={300} weight="semibold">
                                    {t('cashflow.imputations.pendingLabel', 'Saldo pendiente a imputar')}
                                </Text>
                                <Text
                                    size={500}
                                    style={{ color: tokens.colorStatusDangerForeground1, fontWeight: 700 }}
                                >
                                    {pendingAmount.toLocaleString('es-AR', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })} {props.payment.currencyCode ?? ''}
                                </Text>
                            </div>

                            <DebtSelectorComponent
                                debtor={payer!}
                                creditor={receiver}
                                paymentCurrency={props.payment.currencyCode ?? 'ARS'}
                                paymentAmount={pendingAmount}
                                onImputationsChange={setPendingImputations}
                                selectedImputations={pendingImputations}
                            />

                            {props.failOnCreatingImputation && (
                                <MessageBar intent="error" style={{ marginTop: tokens.spacingVerticalS }}>
                                    <MessageBarBody>
                                        {t('cashflow.imputations.createError', 'Error al guardar la imputación.')}
                                    </MessageBarBody>
                                </MessageBar>
                            )}

                            <div className="paymentImputationFormActions">
                                <Button
                                    appearance="secondary"
                                    onClick={handleClose}
                                    disabled={props.isCreatingImputation}
                                >
                                    {t('cashflow.imputations.cancel', 'Cancelar')}
                                </Button>
                                <Button
                                    appearance="primary"
                                    icon={props.isCreatingImputation ? <Spinner size="extra-tiny" /> : undefined}
                                    disabled={pendingImputations.length === 0 || props.isCreatingImputation}
                                    onClick={handleConfirm}
                                >
                                    {t('cashflow.imputations.confirmImputation', 'Confirmar Imputaciones')}
                                </Button>
                            </div>

                            <Separator />
                        </>
                    ) : null}

                </div>
            </DrawerBody>
        </OverlayDrawer>
    );
};

export default ComponentFunctions.connector(PaymentImputationFormComponent);
